import { useState } from "react";
import type { FormEvent } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import type { AccountType } from "@/hooks/useAuth";
import { useToast } from "@/components/feature/Toast";

const ACCOUNT_TYPE_DESTINATIONS: Record<AccountType, string> = {
  buyer: "/",
  seller: "/seller/dashboard",
};

export default function RegisterPage() {
  const { signUp, signInWithGoogle } = useAuth();
  const { showToast } = useToast();
  const navigate = useNavigate();

  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [accountType, setAccountType] = useState<AccountType>("buyer");
  const [showPassword, setShowPassword] = useState(false);
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [confirmationSent, setConfirmationSent] = useState(false);

  const handleGoogleLogin = async () => {
    const { error: oauthError } = await signInWithGoogle();
    if (oauthError) {
      showToast(oauthError, "error");
    }
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError(null);

    if (!fullName.trim()) {
      setError("Por favor, escribe tu nombre completo.");
      return;
    }
    if (!email.trim()) {
      setError("Por favor, escribe tu correo electrónico.");
      return;
    }
    if (password.length < 6) {
      setError("La contraseña debe tener al menos 6 caracteres.");
      return;
    }
    if (!acceptedTerms) {
      setError("Debes aceptar los términos y condiciones para continuar.");
      return;
    }

    setSubmitting(true);
    try {
      const result = await signUp({
        fullName: fullName.trim(),
        email: email.trim(),
        password,
        accountType,
      });

      if (result.error) {
        setError(result.error);
        showToast(result.error, "error");
        return;
      }

      if (result.needsEmailConfirmation) {
        setConfirmationSent(true);
        showToast("Revisa tu correo para confirmar tu cuenta.", "success");
        return;
      }

      showToast("¡Cuenta creada! Bienvenido a VendeYa.", "success");
      navigate(ACCOUNT_TYPE_DESTINATIONS[accountType], { replace: true });
    } catch {
      setError("Ocurrió un error inesperado. Inténtalo de nuevo.");
      showToast("Ocurrió un error inesperado. Inténtalo de nuevo.", "error");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-background-50">
      <div className="mx-auto flex min-h-screen w-full max-w-6xl flex-col lg:flex-row">
        {/* Panel de marca (solo escritorio) */}
        <div className="relative hidden overflow-hidden bg-primary-600 lg:block lg:w-1/2">
          <img
            src="https://readdy.ai/api/search-image?query=Warm%20minimalist%20illustration%20of%20a%20person%20placing%20a%20product%20box%20on%20a%20digital%20marketplace%2C%20surrounded%20by%20floating%20price%20tags%2C%20rating%20stars%2C%20and%20chat%20bubbles%20in%20amber%20and%20emerald%20accents%20over%20a%20deep%20indigo%20gradient%20background%2C%20clean%20editorial%20composition%2C%20soft%20depth%20and%20lighting%2C%20premium%20feel%20for%20a%20classifieds%20platform%20signup&width=1200&height=1400&seq=register-hero-01&orientation=portrait"
            alt="Ilustración de registro en VendeYa"
            className="absolute inset-0 h-full w-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-primary-950/70 via-primary-800/30 to-primary-700/60"></div>

          <div className="relative z-10 flex h-full flex-col justify-between p-12 text-white">
            <Link to="/" className="flex items-center gap-2 whitespace-nowrap">
              <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-white/15 backdrop-blur-sm">
                <i className="ri-store-2-line text-xl text-white"></i>
              </span>
              <span className="font-heading text-xl font-bold">
                Vende<span className="text-accent-400">Ya</span>
              </span>
            </Link>

            <div className="space-y-6">
              <h2 className="font-heading text-3xl font-bold leading-tight">
                Crea tu cuenta y empieza a comprar o vender hoy mismo.
              </h2>
              <p className="max-w-md text-sm leading-relaxed text-white/85">
                Publica tus productos nuevos o usados en minutos y llega a miles
                de compradores en tu zona.
              </p>

              <div className="flex items-center gap-3 rounded-lg border border-white/15 bg-white/10 p-4 backdrop-blur-sm">
                <i className="ri-shield-check-line text-2xl text-accent-400"></i>
                <p className="text-sm leading-snug text-white/90">
                  Elige tu perfil: comprador para explorar, o vendedor para
                  publicar tu propia tienda.
                </p>
              </div>
            </div>

            <p className="text-xs text-white/60">
              © {new Date().getFullYear()} VendeYa. Todos los derechos reservados.
            </p>
          </div>
        </div>

        {/* Formulario */}
        <div className="flex w-full items-center justify-center px-4 py-10 sm:px-6 lg:w-1/2 lg:px-14">
          <div className="w-full max-w-sm">
            {/* Logo móvil */}
            <div className="mb-8 flex flex-col items-center gap-3 lg:hidden">
              <Link to="/" className="flex items-center gap-2 whitespace-nowrap">
                <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary-500">
                  <i className="ri-store-2-line text-xl text-white"></i>
                </span>
                <span className="font-heading text-xl font-bold text-foreground-950">
                  Vende<span className="text-primary-500">Ya</span>
                </span>
              </Link>
            </div>

            <div className="mb-6">
              <h1 className="font-heading text-2xl font-bold text-foreground-950 md:text-3xl">
                Crear cuenta
              </h1>
              <p className="mt-2 text-sm text-foreground-600">
                Regístrate gratis para empezar a vender o comprar.
              </p>
            </div>

            {/* Registro con Google */}
            <button
              type="button"
              onClick={handleGoogleLogin}
              className="mb-4 flex w-full cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-md border border-background-200 bg-background-50 px-4 py-2.5 text-sm font-semibold text-foreground-800 transition-colors hover:border-background-300 hover:bg-background-100"
            >
              <i className="ri-google-fill text-lg text-[#EA4335]"></i>
              Continuar con Google
            </button>

            <div className="mb-5 flex items-center gap-3">
              <span className="h-px flex-1 bg-background-200"></span>
              <span className="text-xs font-medium text-foreground-500">o con tu correo</span>
              <span className="h-px flex-1 bg-background-200"></span>
            </div>

            {confirmationSent ? (
              <div className="flex flex-col items-center gap-4 rounded-lg border border-secondary-200 bg-secondary-50 p-6 text-center">
                <span className="flex h-14 w-14 items-center justify-center rounded-full bg-secondary-100">
                  <i className="ri-mail-send-line text-2xl text-secondary-600"></i>
                </span>
                <h2 className="font-heading text-lg font-bold text-foreground-950">
                  Confirma tu correo
                </h2>
                <p className="text-sm leading-relaxed text-foreground-600">
                  Te enviamos un enlace de confirmación a{" "}
                  <strong className="text-foreground-900">{email}</strong>. Abre el
                  correo y haz clic en el enlace para activar tu cuenta.
                </p>
                <Link
                  to="/login"
                  className="mt-1 cursor-pointer whitespace-nowrap rounded-md bg-primary-500 px-4 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-primary-600"
                >
                  Ir a iniciar sesión
                </Link>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-5" noValidate>
                {error && (
                  <div
                    role="alert"
                    className="flex items-start gap-2 rounded-md border border-accent-200 bg-accent-100 px-3 py-2.5 text-sm text-accent-900"
                  >
                    <i className="ri-error-warning-fill mt-0.5 text-accent-600"></i>
                    <span className="leading-snug">{error}</span>
                  </div>
                )}

                <div>
                  <label
                    htmlFor="fullName"
                    className="mb-1.5 block text-sm font-medium text-foreground-700"
                  >
                    Nombre completo
                  </label>
                  <div className="relative">
                    <span className="pointer-events-none absolute inset-y-0 left-0 flex w-10 items-center justify-center text-foreground-400">
                      <i className="ri-user-line text-base"></i>
                    </span>
                    <input
                      id="fullName"
                      name="fullName"
                      type="text"
                      autoComplete="name"
                      value={fullName}
                      onChange={(e) => setFullName(e.target.value)}
                      placeholder="Tu nombre y apellido"
                      className="w-full rounded-md border border-background-200 bg-background-50 py-2.5 pl-10 pr-3 text-sm text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
                    />
                  </div>
                </div>

                <div>
                  <label
                    htmlFor="email"
                    className="mb-1.5 block text-sm font-medium text-foreground-700"
                  >
                    Correo electrónico
                  </label>
                  <div className="relative">
                    <span className="pointer-events-none absolute inset-y-0 left-0 flex w-10 items-center justify-center text-foreground-400">
                      <i className="ri-mail-line text-base"></i>
                    </span>
                    <input
                      id="email"
                      name="email"
                      type="email"
                      autoComplete="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="tu@correo.com"
                      className="w-full rounded-md border border-background-200 bg-background-50 py-2.5 pl-10 pr-3 text-sm text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
                    />
                  </div>
                </div>

                <div>
                  <label
                    htmlFor="password"
                    className="mb-1.5 block text-sm font-medium text-foreground-700"
                  >
                    Contraseña
                  </label>
                  <div className="relative">
                    <span className="pointer-events-none absolute inset-y-0 left-0 flex w-10 items-center justify-center text-foreground-400">
                      <i className="ri-lock-line text-base"></i>
                    </span>
                    <input
                      id="password"
                      name="password"
                      type={showPassword ? "text" : "password"}
                      autoComplete="new-password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="Mínimo 6 caracteres"
                      className="w-full rounded-md border border-background-200 bg-background-50 py-2.5 pl-10 pr-11 text-sm text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword((v) => !v)}
                      aria-label={showPassword ? "Ocultar contraseña" : "Mostrar contraseña"}
                      className="absolute inset-y-0 right-0 flex w-11 cursor-pointer items-center justify-center text-foreground-400 transition-colors hover:text-foreground-700"
                    >
                      <i
                        className={`text-base ${showPassword ? "ri-eye-off-line" : "ri-eye-line"}`}
                      ></i>
                    </button>
                  </div>
                </div>

                {/* Selección de tipo de cuenta */}
                <div>
                  <span className="mb-1.5 block text-sm font-medium text-foreground-700">
                    ¿Qué quieres hacer en VendeYa?
                  </span>
                  <div className="grid grid-cols-2 gap-1 rounded-full border border-background-200 bg-background-100 p-1">
                    <button
                      type="button"
                      onClick={() => setAccountType("buyer")}
                      className={`flex cursor-pointer items-center justify-center gap-1.5 whitespace-nowrap rounded-full px-3 py-2 text-sm font-medium transition-colors ${
                        accountType === "buyer"
                          ? "bg-primary-500 text-white"
                          : "text-foreground-600 hover:text-foreground-900"
                      }`}
                    >
                      <i className="ri-shopping-bag-3-line text-base"></i>
                      Comprar
                    </button>
                    <button
                      type="button"
                      onClick={() => setAccountType("seller")}
                      className={`flex cursor-pointer items-center justify-center gap-1.5 whitespace-nowrap rounded-full px-3 py-2 text-sm font-medium transition-colors ${
                        accountType === "seller"
                          ? "bg-primary-500 text-white"
                          : "text-foreground-600 hover:text-foreground-900"
                      }`}
                    >
                      <i className="ri-store-2-line text-base"></i>
                      Vender
                    </button>
                  </div>
                </div>

                <label className="flex cursor-pointer items-start gap-2 text-sm text-foreground-600">
                  <input
                    type="checkbox"
                    checked={acceptedTerms}
                    onChange={(e) => setAcceptedTerms(e.target.checked)}
                    className="mt-0.5 h-4 w-4 cursor-pointer rounded border-background-300 accent-primary-500"
                  />
                  <span>
                    Acepto los{" "}
                    <span className="font-semibold text-foreground-900">
                      términos y condiciones
                    </span>{" "}
                    y la política de privacidad.
                  </span>
                </label>

                <button
                  type="submit"
                  disabled={submitting}
                  className="flex w-full cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-4 py-2.5 text-sm font-semibold text-white transition-all hover:bg-primary-600 active:scale-[0.99] disabled:cursor-not-allowed disabled:opacity-70"
                >
                  {submitting && (
                    <i className="ri-loader-4-line animate-spin text-base"></i>
                  )}
                  {submitting ? "Creando cuenta..." : "Crear cuenta"}
                </button>
              </form>
            )}

            {!confirmationSent && (
              <p className="mt-6 text-center text-sm text-foreground-600">
                ¿Ya tienes una cuenta?{" "}
                <Link
                  to="/login"
                  className="font-semibold text-primary-500 transition-colors hover:text-primary-600"
                >
                  Inicia sesión
                </Link>
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}